import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { listSupportDriverBookings, getSupportDriverBookingDetail } from "../../../../../api/adminSupportApi";

/* --------------------- helpers --------------------- */
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function up(x) {
  return safeStr(x).toUpperCase();
}

/**
 * ✅ Consistent format everywhere:
 * "05 Feb 2026, 8:15 AM"
 */
const DT_FMT = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function toDateSafe(v) {
  if (!v) return null;
  try {
    const d = new Date(String(v));
    if (!Number.isNaN(d.getTime())) return d;
  } catch (_) {}
  return null;
}

function fmtDT(v) {
  const d = toDateSafe(v);
  if (!d) return v ? String(v) : "—";
  return DT_FMT.format(d);
}

/**
 * Handles pickupDate+pickupTime OR dropDate+dropTime consistently.
 * - pickupDate: "2026-02-05" OR "05/02/2026" (best: yyyy-mm-dd)
 * - pickupTime: "08:15" OR "08:15:30"
 */
function fmtDTFromParts(datePart, timePart) {
  const dp = safeStr(datePart);
  const tp = safeStr(timePart);
  if (!dp && !tp) return "—";
  // Try ISO-like first (yyyy-mm-dd)
  if (dp && tp && /^\d{4}-\d{2}-\d{2}$/.test(dp)) {
    const iso = `${dp}T${tp}`;
    const d = toDateSafe(iso);
    if (d) return DT_FMT.format(d);
  }
  // Try date only if looks ISO
  if (dp && /^\d{4}-\d{2}-\d{2}$/.test(dp)) {
    const d = toDateSafe(`${dp}T00:00:00`);
    if (d) return DT_FMT.format(d);
  }
  // Fallback: show as entered (still better than wrong parsing)
  if (dp && tp) return `${dp} ${tp}`;
  return dp || tp || "—";
}

function oneLine(s, fallback = "—") {
  const x = safeStr(s);
  return x ? x : fallback;
}
function numStr(v, fallback = "—") {
  if (v == null) return fallback;
  const s = String(v);
  return s.trim() ? s : fallback;
}

function Chip({ children, tone = "neutral" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <span className={classNames("inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-semibold", cls)}>
      {children}
    </span>
  );
}

function toneBookingStatus(s) {
  const v = up(s);
  if (v === "COMPLETED" || v === "ENDED") return "good";
  if (v === "CANCELLED" || v === "UNFULFILLED") return "bad";
  if (v === "CONFIRMED" || v === "BOOKED") return "warn";
  return "neutral";
}
function toneRideStatus(s) {
  const v = up(s);
  if (v === "COMPLETED" || v === "ENDED") return "good";
  if (v === "CANCELLED") return "bad";
  if (v === "ARRIVED_AT_PICKUP" || v === "IN_PROGRESS") return "warn";
  return "neutral";
}
function tonePaymentStatus(s) {
  const v = up(s);
  if (v === "PAID" || v === "CAPTURED") return "good";
  if (v === "FAILED" || v === "CANCELLED") return "bad";
  if (v === "CREATED" || v === "PENDING") return "warn";
  return "neutral";
}

function Drawer({ open, onClose, title, children, widthClass = "max-w-5xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={classNames("absolute right-0 top-0 h-full w-full bg-white shadow-xl", widthClass)}>
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="truncate text-[14px] font-extrabold text-slate-900">{title}</div>
            <div className="mt-0.5 text-[12px] text-slate-500">Full booking context for driver support</div>
          </div>
          <button
            className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="h-[calc(100%-73px)] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-[13px] font-semibold text-slate-900 truncate">{oneLine(value)}</div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
        {right}
      </div>
      <div className="px-4 py-4">{children}</div>
    </div>
  );
}

function PaginationFooter({ page, totalPages, size, onSize, onPrev, onNext, totalElements }) {
  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          Page <span className="font-semibold text-slate-700">{page + 1}</span> of{" "}
          <span className="font-semibold text-slate-700">{Math.max(1, totalPages)}</span>
          {Number.isFinite(Number(totalElements)) ? (
            <>
              {" "}
              <span className="text-slate-300">•</span>{" "}
              <span className="font-semibold text-slate-700">{Number(totalElements).toLocaleString("en-IN")}</span> total
            </>
          ) : null}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold text-slate-500">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => onSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50 outline-none"
              >
                {[10, 20, 30, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">▼</span>
            </div>
          </div>

          <div className="text-sm text-slate-700">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={page <= 0}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={page + 1 >= totalPages}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** small pool runner to avoid N+1 burst */
async function runPool(items, worker, concurrency = 6) {
  const q = [...items];
  const runners = Array.from({ length: Math.max(1, concurrency) }).map(async () => {
    while (q.length) {
      const it = q.shift();
      // eslint-disable-next-line no-await-in-loop
      await worker(it);
    }
  });
  await Promise.all(runners);
}

export default function SupportDriverBookingsTab() {
  const ctx = useOutletContext() || {};
  const driverId = ctx.driverId;

  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [openBookingId, setOpenBookingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ✅ Ride status cache fetched via SAME detail endpoint used by drawer
  // shape: { [bookingId]: { status, rideId, updatedAt } | null }
  const [rideMetaByBookingId, setRideMetaByBookingId] = useState({});
  const [rideMetaLoading, setRideMetaLoading] = useState({}); // { [bookingId]: true }
  const rideMetaByBookingIdRef = useRef({});
  useEffect(() => {
    rideMetaByBookingIdRef.current = rideMetaByBookingId;
  }, [rideMetaByBookingId]);

  const rows = data?.content || [];
  const totalPages = Number(data?.totalPages ?? 1);
  const totalElements = Number(data?.totalElements ?? 0);

  const queryKey = useMemo(() => `${driverId}|${status}|${q}|${page}|${size}`, [driverId, status, q, page, size]);

  useEffect(() => {
    if (!driverId) return;
    let alive = true;

    setLoading(true);
    setErr("");

    listSupportDriverBookings(driverId, {
      status: status && status !== "ALL" ? status : undefined,
      q: safeStr(q) || undefined,
      page,
      size,
    })
      .then((res) => {
        if (!alive) return;
        setData(res);
      })
      .catch((e) => alive && setErr(e?.message || "Failed to load bookings"))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [queryKey, driverId]);

  // ✅ Prefetch ride status for current page rows using getSupportDriverBookingDetail (same as drawer)
  useEffect(() => {
    if (!driverId) return;
    if (!Array.isArray(rows) || rows.length === 0) return;

    let alive = true;

    const bookingIds = rows
      .map((r) => r?.bookingId)
      .filter((id) => id != null);

    // fetch only missing (not present in cache)
    const missing = bookingIds.filter((id) => {
      const cache = rideMetaByBookingIdRef.current;
      return !(id in cache);
    });

    if (missing.length === 0) return;

    // mark loading flags
    setRideMetaLoading((prev) => {
      const next = { ...prev };
      missing.forEach((id) => {
        next[id] = true;
      });
      return next;
    });

    runPool(
      missing,
      async (bookingId) => {
        try {
          const d = await getSupportDriverBookingDetail(driverId, bookingId);
          if (!alive) return;

          const latestRide = d?.latestRide || null;
          const meta =
            latestRide && (latestRide.status || latestRide.rideId)
              ? {
                  status: latestRide.status || null,
                  rideId: latestRide.rideId || null,
                  updatedAt: latestRide.updatedAt || null,
                }
              : null;

          setRideMetaByBookingId((prev) => ({ ...prev, [bookingId]: meta }));
        } catch (_) {
          if (!alive) return;
          setRideMetaByBookingId((prev) => ({ ...prev, [bookingId]: null }));
        } finally {
          if (!alive) return;
          setRideMetaLoading((prev) => {
            const next = { ...prev };
            delete next[bookingId];
            return next;
          });
        }
      },
      6
    );

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId, rows]);

  useEffect(() => {
    if (!openBookingId || !driverId) return;
    let alive = true;

    setDetail(null);
    setDetailLoading(true);

    getSupportDriverBookingDetail(driverId, openBookingId)
      .then((res) => alive && setDetail(res))
      .catch(() => alive && setDetail(null))
      .finally(() => alive && setDetailLoading(false));

    return () => {
      alive = false;
    };
  }, [openBookingId, driverId]);

  // allow ESC close drawer
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpenBookingId(null);
    }
    if (!!openBookingId) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openBookingId]);

  const selectedBooking = detail?.booking || null;
  const latestRide = detail?.latestRide || null;

  // ✅ schedule cell now uses the same consistent formatter
  function pickupTimeCell(r) {
    if (r.pickupDate && r.pickupTime) return fmtDTFromParts(r.pickupDate, r.pickupTime);
    return r.pickupAt ? fmtDT(r.pickupAt) : "—";
  }

  function getRideStatusForRow(r) {
    const bookingId = r?.bookingId;
    const meta = bookingId != null ? rideMetaByBookingId[bookingId] : undefined;
    if (meta && meta.status) return meta.status;

    const rideStatus = r?.rideStatus || r?.latestRideStatus || r?.ride_state || null;
    return rideStatus || null;
  }

  function rideStatusCell(r) {
    const bookingId = r?.bookingId;
    const isLoadingRide = bookingId != null ? !!rideMetaLoading[bookingId] : false;
    const rideStatus = getRideStatusForRow(r);

    if (isLoadingRide && !rideStatus) {
      return <Chip tone="neutral">LOADING…</Chip>;
    }
    return <Chip tone={toneRideStatus(rideStatus)}>{up(rideStatus) || "—"}</Chip>;
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="border-b border-slate-200 px-5 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Bookings</div>
          <div className="mt-0.5 text-[12px] text-slate-500">
            {loading ? "Loading…" : `${totalElements.toLocaleString("en-IN")} total`}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Search bookingId / vehicle / owner / user / payment / razorpay ids"
            className="h-10 w-full sm:w-[360px] rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
          />

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
          >
            {["ALL", "CONFIRMED", "COMPLETED", "CANCELLED", "UNFULFILLED", "BOOKED", "STARTED", "ONGOING", "IN_PROGRESS"].map(
              (s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              )
            )}
          </select>
        </div>
      </div>

      {err ? (
        <div className="border-b border-slate-200 px-5 py-3 text-[12px] font-semibold text-rose-600">{err}</div>
      ) : null}

      <div className="w-full overflow-x-auto">
        <table className="min-w-[1200px] w-full table-fixed">
          <colgroup>
            <col style={{ width: "110px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "160px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "180px" }} />
            <col style={{ width: "220px" }} />
            <col style={{ width: "220px" }} />
            <col style={{ width: "160px" }} />
            <col style={{ width: "140px" }} />
          </colgroup>

          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Booking ID</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Booking Status</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Ride Status</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Vehicle</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Owner ID</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Owner</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Pickup</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Drop</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Schedule</th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Payment</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-6 py-10 text-sm font-semibold text-slate-600">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-14 text-center">
                  <div className="text-sm font-semibold text-slate-900">No bookings</div>
                  <div className="mt-1 text-[12px] text-slate-500">Try changing filters or search.</div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.bookingId}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenBookingId(r.bookingId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setOpenBookingId(r.bookingId);
                  }}
                  className="cursor-pointer hover:bg-slate-50 focus:outline-none"
                  title="Click to open details"
                >
                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 tabular-nums">{numStr(r.bookingId)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <Chip tone={toneBookingStatus(r.status)}>{up(r.status) || "—"}</Chip>
                  </td>

                  <td className="px-4 py-4">{rideStatusCell(r)}</td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 tabular-nums">{oneLine(r.vehicleRegNo)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 tabular-nums">{numStr(r.ownerId)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{oneLine(r.ownerName)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{oneLine(r.pickupLocation)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">
                      {oneLine(r.destination || (Array.isArray(r.destinations) && r.destinations[0]) || "—")}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[12px] font-semibold text-slate-900">{pickupTimeCell(r)}</div>
                  </td>

                  <td className="px-4 py-4">
                    <Chip tone={tonePaymentStatus(r.paymentStatus)}>{up(r.paymentStatus) || "—"}</Chip>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationFooter
        page={page}
        totalPages={totalPages}
        size={size}
        onSize={(n) => {
          setSize(n);
          setPage(0);
        }}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        totalElements={totalElements}
      />

      <Drawer
        open={!!openBookingId}
        onClose={() => setOpenBookingId(null)}
        widthClass="max-w-5xl"
        title={openBookingId ? `Booking #${openBookingId}` : "Booking"}
      >
        {detailLoading ? (
          <div className="p-6 text-sm font-semibold text-slate-600">Loading…</div>
        ) : !detail ? (
          <div className="p-6 text-sm font-semibold text-slate-600">No detail</div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip tone={toneBookingStatus(selectedBooking?.status)}>{up(selectedBooking?.status) || "—"}</Chip>
                  <Chip tone={toneRideStatus(latestRide?.status)}>{up(latestRide?.status) || "NO RIDE"}</Chip>
                  <Chip tone={tonePaymentStatus(selectedBooking?.paymentStatus)}>{up(selectedBooking?.paymentStatus) || "—"}</Chip>
                  {selectedBooking?.selfDrive ? <Chip tone="neutral">SELF DRIVE</Chip> : null}
                </div>
                <div className="text-[12px] text-slate-600">
                  Created: <span className="font-semibold">{fmtDT(selectedBooking?.createdAt)}</span>{" "}
                  <span className="text-slate-300">•</span> Updated:{" "}
                  <span className="font-semibold">{fmtDT(selectedBooking?.updatedAt)}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <Section title="User overview">
                <div className="grid grid-cols-1 gap-3">
                  <KV label="User ID" value={selectedBooking?.userId} />
                  <KV label="User phone" value={selectedBooking?.userPhone} />
                  <KV label="User email" value={selectedBooking?.userEmail} />
                </div>
              </Section>

              <Section title="Owner overview">
                <div className="grid grid-cols-1 gap-3">
                  <KV label="Owner ID" value={selectedBooking?.ownerId} />
                  <KV label="Owner name" value={selectedBooking?.ownerName} />
                  <KV label="Owner phone" value={selectedBooking?.ownerPhone} />
                </div>
              </Section>

              <Section title="Vehicle overview">
                <div className="grid grid-cols-1 gap-3">
                  <KV label="Vehicle reg no" value={selectedBooking?.vehicleRegNo} />
                  <KV label="Assigned driver ID" value={selectedBooking?.assignedDriverId} />
                  <KV label="Assigned driver phone" value={selectedBooking?.assignedDriverPhone} />
                </div>
              </Section>
            </div>

            <Section
              title="Booking schedule & route"
              right={
                <div className="flex items-center gap-2">
                  <div className="text-[12px] text-slate-500">Ride OTP</div>
                  <div className="text-[12px] font-extrabold text-slate-900 tabular-nums">
                    {selectedBooking?.rideOtp ? selectedBooking.rideOtp : "—"}
                  </div>
                </div>
              }
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <KV label="Pickup location" value={selectedBooking?.pickupLocation} />
                <KV
                  label="Drop location"
                  value={selectedBooking?.destination || (selectedBooking?.destinations?.[0] ?? "—")}
                />
                <KV
                  label="Pickup time"
                  value={
                    selectedBooking?.pickupDate && selectedBooking?.pickupTime
                      ? fmtDTFromParts(selectedBooking.pickupDate, selectedBooking.pickupTime)
                      : fmtDT(selectedBooking?.pickupAt)
                  }
                />
                <KV
                  label="Drop time"
                  value={
                    selectedBooking?.dropDate && selectedBooking?.dropTime
                      ? fmtDTFromParts(selectedBooking.dropDate, selectedBooking.dropTime)
                      : fmtDT(selectedBooking?.dropAt)
                  }
                />
              </div>
            </Section>

            <Section title="Ride details">
              {latestRide?.rideId ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <KV label="Ride ID" value={latestRide?.rideId} />
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Ride status</div>
                    <div className="mt-1">
                      <Chip tone={toneRideStatus(latestRide?.status)}>{up(latestRide?.status) || "—"}</Chip>
                    </div>
                  </div>
                  <KV label="Ride last updated" value={fmtDT(latestRide?.updatedAt)} />
                </div>
              ) : (
                <div className="text-sm font-semibold text-slate-600">No ride linked to this booking.</div>
              )}
            </Section>

            <Section title="Payment">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <KV label="Payment ID" value={selectedBooking?.paymentId} />
                <KV label="Razorpay order ID" value={selectedBooking?.razorpayOrderId} />
                <KV label="Razorpay payment ID" value={selectedBooking?.razorpayPaymentId} />
                <KV label="Payment status" value={up(selectedBooking?.paymentStatus) || "—"} />
              </div>
            </Section>

            <Section title="Request & offer">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-[12px] font-bold text-slate-700">Request</div>
                  <div className="mt-2 grid grid-cols-1 gap-3">
                    <KV label="Request ID" value={detail.request?.requestId} />
                    <KV label="Request status" value={detail.request?.status} />
                    <KV label="Request payment status" value={detail.request?.paymentStatus} />
                    <KV label="Match batch no" value={detail.request?.matchBatchNo} />
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-[12px] font-bold text-slate-700">Offer</div>
                  <div className="mt-2 grid grid-cols-1 gap-3">
                    <KV label="Match ID" value={detail.offer?.matchId} />
                    <KV label="Offer status" value={detail.offer?.status} />
                    <KV label="Offer batch no" value={detail.offer?.batchNo} />
                    <KV label="Offer vehicle" value={detail.offer?.vehicleRegNo} />
                  </div>
                </div>
              </div>
            </Section>

            <Section title="Request events">
              {Array.isArray(detail.requestEvents) && detail.requestEvents.length ? (
                <div className="space-y-2">
                  {detail.requestEvents.slice(0, 50).map((ev) => (
                    <div key={ev.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2 text-[12px]">
                        <span className="font-extrabold text-slate-900">{ev.eventType || "EVENT"}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-slate-600">{fmtDT(ev.createdAt)}</span>
                        {ev.traceId ? (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className="text-slate-500 tabular-nums">{ev.traceId}</span>
                          </>
                        ) : null}
                      </div>
                      {ev.message ? <div className="mt-1 text-[12px] text-slate-700">{ev.message}</div> : null}
                      <div className="mt-1 text-[11px] text-slate-500 tabular-nums">
                        {oneLine(ev.actorType)} {ev.actorId != null ? `#${ev.actorId}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm font-semibold text-slate-600">No events</div>
              )}
            </Section>
          </div>
        )}
      </Drawer>
    </div>
  );
}
